import { ENTITY_TYPES, LocationDTO } from '@sales-center/common'
import React, { useCallback, useEffect } from 'react'
import { AttachmentsDrawerContents } from './AttachmentsDrawerContents'
import { IAttachmentToAgreementDTO } from '@user/store/attachments'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import Typography from '@mui/material/Typography'
import {
  setIsSelectionEnable,
  setSelectedAttachments,
  useSelectedAttachmentStore,
} from './selectedAttachmentStore'

type Props = {
  entityId: number
  entityType: ENTITY_TYPES
  onClose: () => void
  open: boolean
  canUpdate: boolean
  onSelectionComplete?: (attachments: IAttachmentToAgreementDTO[]) => void
  currentServiceLocation?: LocationDTO
}

export function AttachmentsDrawer(props: Props) {
  const { onClose, open, onSelectionComplete, currentServiceLocation } = props
  const { isSelectionEnable, selectedAttachments } =
    useSelectedAttachmentStore()

  useEffect(() => {
    if (open) {
      setIsSelectionEnable(!!onSelectionComplete)
    }
    if (!open) {
      setIsSelectionEnable(!!onSelectionComplete)
      setSelectedAttachments([])
    }
  }, [onSelectionComplete, open])

  const onSelectionCompleteHandler = useCallback(() => {
    if (onSelectionComplete) {
      onSelectionComplete([...selectedAttachments])
      setSelectedAttachments([])
      onClose()
    }
  }, [selectedAttachments, onSelectionComplete, setSelectedAttachments])

  return (
    <Dialog
      scroll="paper"
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: {
            xs: '95vw',
            sm: '501px',
          },
          maxWidth: {
            xs: '95vw',
            sm: '501px',
          },
          height: 'calc(100vh - 64px)',
          maxHeight: '706px',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 0,
          height: '72px',
          pl: '24px',
          pr: '15px',
        }}
      >
        <Typography
          variant={'h6'}
          sx={{
            color: (theme) => theme.palette.text.primary,
          }}
        >
          Attachments
        </Typography>

        <IconButton
          onClick={onClose}
          size={'medium'}
          sx={{
            color: (theme) => theme.palette.action.active,
            cursor: 'pointer',
            borderRadius: '100px',
          }}
          data-testid="action-modal-close-btn"
          data-test-id="attachmentsDrawerCloseBtn"
        >
          <CloseIcon fontSize={'medium'} />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          p: 0,
          position: 'relative',
          pb: '78px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          <AttachmentsDrawerContents
            {...props}
            currentServiceLocation={currentServiceLocation}
          />
        </Box>
        <Box
          sx={{
            height: '78px',
            position: 'absolute',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            padding: (theme) => theme.spacing(1.7),
            width: '100%',
            left: 0,
            bottom: 0,
            gap: 1.6,
            backgroundColor: 'background.paper',
          }}
        >
          {isSelectionEnable ? (
            <>
              <Button
                variant={'text'}
                onClick={onClose}
                data-test-id={'attachmentCloseButton'}
                sx={{
                  height: '36px',
                }}
              >
                Close
              </Button>
              <Button
                color="primary"
                variant="contained"
                disabled={selectedAttachments.length === 0 ? true : false}
                sx={{
                  height: '36px',
                }}
                onClick={onSelectionCompleteHandler}
                data-test-id={'attachmentAddToAgreementButton'}
              >
                Add to Agreement
              </Button>
            </>
          ) : (
            <Button
              color="primary"
              variant="contained"
              sx={{
                height: '36px',
              }}
              onClick={onClose}
              data-test-id={'attachmentCloseButton'}
            >
              Close
            </Button>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  )
}

AttachmentsDrawer.displayName = 'AttachmentsDrawer'
